/**
 * @packageDocumentation
 * @module f1/fn
 */

export interface Once {
  /**
   * Creates a function that is restricted to invoking passed function once.
   *
   * @public
   * @function f1.once
   * @param fn the function to limit to running once
   * @example
   *
   * ```ts
   * let a = 1;
   *
   * const onceF = f1.once((v) => v + a);
   *
   * onceF(1) => 2
   * mimic([ 'a' ]) => []
   * mimic('abcde') => 'abcde'
   * ```
   */
  <FN extends Function, V>(fn: FN): FN;
}

/**
 * @see Once
 */
export default <FN extends Function, V>(fn: FN): FN => {
  let called = false;
  let val: V;

  return (function (...args: unknown[]) {
    if (!called) {
      return (val = fn(...args));
    }

    called = true;
    return val;
  } as unknown) as FN;
};
